import { Credentials } from '@quadminds/client-apis/auth';
import { isDevelopment } from '@quadminds/shared/utils';
import {
    GoogleAuthProvider,
    OAuthProvider,
    UserCredential,
    createUserWithEmailAndPassword,
    fetchSignInMethodsForEmail,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
} from 'firebase/auth';

import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from './config';
import { BASE_URL } from '../../config';

export class FirebaseService {
    public static async signInWithGoogle(emailHint?: string): Promise<UserCredential> {
        const googleProvider = new GoogleAuthProvider();
        if (emailHint) {
            googleProvider.setCustomParameters({
                login_hint: emailHint,
            });
        }

        try {
            const res = await signInWithPopup(auth, googleProvider);
            return res;
        } catch (error: unknown) {
            console.error(error);
            throw error;
        }
    }

    public static async signInWithMicrosoft(): Promise<UserCredential> {
        const provider = new OAuthProvider('microsoft.com');
        try {
            return await signInWithPopup(auth, provider);
        } catch (error: unknown) {
            console.error(error);
            throw error;
        }
    }

    public static async signInWithApple(): Promise<UserCredential> {
        const provider = new OAuthProvider('apple.com');
        try {
            return await signInWithPopup(auth, provider);
        } catch (error: unknown) {
            console.error(error);
            throw error;
        }
    }

    public static async registerWithEmailAndPassword(credentials: Credentials): Promise<UserCredential> {
        try {
            return await createUserWithEmailAndPassword(auth, credentials.email, credentials.password);
        } catch (error: unknown) {
            console.error(error);
            throw error;
        }
    }

    public static async logInWithEmailAndPassword(credentials: Credentials): Promise<UserCredential> {
        try {
            return await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
        } catch (error: unknown) {
            console.error(error);
            throw error;
        }
    }

    public static async sendPasswordReset(email: string): Promise<boolean> {
        try {
            await sendPasswordResetEmail(auth, email);
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    public static async checkProvider(email: string): Promise<string[]> {
        try {
            return await fetchSignInMethodsForEmail(auth, email);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    public static async sendVerificationEmail(email: string, redirectUrl?: string) {
        if (!redirectUrl) {
            redirectUrl = window.location.origin + BASE_URL + '/verification-success';
        }

        const functions = getFunctions();

        // Emulates in local env
        // if (isDevelopment()) connectFunctionsEmulator(functions, 'localhost', 5001);

        const customVerifyEmail = httpsCallable(functions, 'verifyEmail');
        return await customVerifyEmail({ email, redirectUrl });
    }

    // TODO: finish... not used right now
    public static async sendWelcomeEmail(email: string) {
        const functions = getFunctions();

        // Emulates in local env
        if (isDevelopment()) connectFunctionsEmulator(functions, 'localhost', 5001);

        // TODO: check typology to choose welcome email
        const welcomeEmail = httpsCallable(functions, 'welcomeMerchant');
        return await welcomeEmail({ email });
    }

    public static async logout() {
        await signOut(auth);

        // Prevent the browser from silently reusing stored credentials on the next visit.
        if (typeof navigator !== 'undefined' && navigator.credentials?.preventSilentAccess) {
            await navigator.credentials.preventSilentAccess().catch(() => undefined);
        }
    }
}
